import { useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

/**
 * Role-based route access control map.
 * Key = route prefix, Value = array of allowed roles (empty = all authenticated users).
 * More specific paths should be listed before broader ones.
 */
const ROUTE_ROLE_MAP: { prefix: string; allowedRoles: string[] }[] = [
  // Settings — admin only
  { prefix: "/settings/", allowedRoles: ["admin"] },
  { prefix: "/settings", allowedRoles: ["admin"] },

  // HO Dashboard — HO staff + branch management + deputy + admin
  {
    prefix: "/ho-dashboard",
    allowedRoles: ["admin", "branch_management_staff", "deputy_director", "ho_staff", "ho_manager"],
  },
  {
    prefix: "/ho-",
    allowedRoles: ["admin", "branch_management_staff", "deputy_director", "ho_staff", "ho_manager"],
  },

  // Reports — management roles only
  {
    prefix: "/reports",
    allowedRoles: [
      "admin",
      "credit_analyst",
      "credit_manager",
      "branch_manager",
      "branch_management_staff",
      "deputy_director",
      "ho_staff",
      "ho_manager",
    ],
  },

  // User Management — admin only
  { prefix: "/user-management", allowedRoles: ["admin"] },

  // Activity Log — admin + managers
  {
    prefix: "/activity-log",
    allowedRoles: ["admin", "branch_manager", "branch_management_staff", "deputy_director", "ho_manager"],
  },
];

interface RoleGuardProps {
  children: React.ReactNode;
}

/**
 * RoleGuard wraps protected routes and redirects unauthorized users to /dashboard
 * with a toast notification.
 */
export function RoleGuard({ children }: RoleGuardProps) {
  const { user, loading } = useAuth();
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (loading || !user) return;

    const userRole = user.role ?? "user";

    // Find the most specific matching rule for the current path
    const matchedRule = ROUTE_ROLE_MAP.find(rule =>
      location === rule.prefix || location.startsWith(rule.prefix)
    );

    if (!matchedRule) return; // No restriction for this path

    const isAllowed = matchedRule.allowedRoles.includes(userRole);
    if (!isAllowed) {
      toast.error("ທ່ານບໍ່ມີສິດເຂົ້າໜ້ານີ້", {
        description: "ກະລຸນາຕິດຕໍ່ Admin ຖ້າທ່ານຕ້ອງການສິດເຂົ້າໃຊ້",
        duration: 4000,
      });
      setLocation("/dashboard");
    }
  }, [location, user, loading, setLocation]);

  // While loading, render children (ProtectedRoute already shows spinner)
  return <>{children}</>;
}
